/**
 * Panel araçları özeti — `GET /api/integrations/panel-fleet?day=YYYY-MM-DD&days=N`.
 *
 * Seferler ekranındaki "Panel araçları — bugün" kartı ve Araçlar & Ekip ekranındaki panel
 * araçları bölümü bu sorguyu PAYLAŞIR (60 sn yenileme). Defter DEĞİŞMEZ; salt okunur.
 *
 * Sorumluluk ayrımı: HAYAT_PANEL ve ABANT_PANEL siparişlerini `externalVehicleRaw`a göre gruplayıp
 * panelin ham durumunu sayım + açık sipariş listesi olarak döner. Bu modül sadece istemcidir;
 * kurallar sunucu tarafında (`apps/api/src/integrations/fleet/panel-fleet.service.ts`).
 *
 * `PanelFleetResponse` tipleri doğrudan sunucu sözleşmesinden üretildi — ek alan eklenmez,
 * `types.ts`'e DOKUNULMAZ (görev kapsamı).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './client';

/** Sunucu yanıt türleri (`panel-fleet.service.ts` ile birebir). */

export type PanelFleetSource = 'HAYAT_PANEL' | 'ABANT_PANEL';
export type PanelFleetTone = 'new' | 'progress' | 'done' | 'cancelled';

export interface PanelFleetOpenOrder {
  id: string;
  receivedAt: string;
  customerName: string;
  address: string;
  stageText: string;
  totalAmount: string;
}

export interface PanelFleetGroup {
  source: PanelFleetSource;
  label: string | null;
  displayName: string | null;
  vehicle: { id: string; plate: string } | null;
  counts: Record<PanelFleetTone, number>;
  deliveredAmount: string | null;
  lastActivityAt: string | null;
  open: PanelFleetOpenOrder[];
}

export interface PanelFleetResponse {
  day: string;
  days: number;
  sources: PanelFleetSource[];
  groups: PanelFleetGroup[];
}

/**
 * Panel araçları özeti sorgusu.
 *
 * Yenileme: 60 sn. Seferler ekranındaki kart her dakika arka planda tazelenir; elle yenilemek
 * için `refetch()` kullanılabilir. `enabled` parametresi: gün verilmeden de bugün için çalışır;
 * üst bileşen kullanıcı rolü OWNER/MANAGER değilse `enabled: false` yapar.
 *
 * `day` (Istanbul `YYYY-MM-DD`) ve `days` (1..31) isteğe bağlı; varsayılanlar sunucuda.
 */
export function usePanelFleet(opts?: { day?: string; days?: number; enabled?: boolean }) {
  const params = new URLSearchParams();
  if (opts?.day) params.set('day', opts.day);
  if (opts?.days !== undefined) params.set('days', String(opts.days));
  const qs = params.toString();
  const url = qs ? `/api/integrations/panel-fleet?${qs}` : '/api/integrations/panel-fleet';
  return useQuery({
    queryKey: ['panel-fleet', opts?.day ?? null, opts?.days ?? null],
    queryFn: () => api.get<PanelFleetResponse>(url),
    refetchInterval: 60_000,
    enabled: opts?.enabled ?? true,
  });
}

/** Mahalle → araç öneri sunucu yanıt türleri (`panel-fleet-suggestions.service.ts` ile birebir). */
export interface PanelFleetSuggestionRow {
  source: PanelFleetSource;
  neighborhood: string;
  vehicleLabel: string;
  displayName: string | null;
  vehicle: { id: string; plate: string } | null;
  samples: number;
  total: number;
  sharePct: number;
}

export interface PanelFleetSuggestionOpenOrder {
  orderId: string;
  source: PanelFleetSource;
  neighborhood: string | null;
  suggestion: {
    vehicleLabel: string;
    samples: number;
    total: number;
    sharePct: number;
  } | null;
}

export interface PanelFleetSuggestionsResponse {
  days: number;
  table: PanelFleetSuggestionRow[];
  openOrders: PanelFleetSuggestionOpenOrder[];
}

/**
 * Mahalle → panel aracı öneri sorgusu.
 *
 * Yalnız OKUR — siparişe, sefere ya da panele YAZMAZ. Defter değişmez. Patron yeni gelen
 * siparişe hangi aracın gideceğini bu öneriden seçer (ileride otomatik atama temeli).
 *
 * Yenileme: 60 sn (panel kartı her dakika yenilenir; ekran açıkken öneri eskimesin).
 * `days` (7..90) sunucu tarafında kırpılır; varsayılan 30.
 */
export function usePanelFleetSuggestions(opts?: { days?: number; enabled?: boolean }) {
  const params = new URLSearchParams();
  if (opts?.days !== undefined) params.set('days', String(opts.days));
  const qs = params.toString();
  const url = qs
    ? `/api/integrations/panel-fleet/suggestions?${qs}`
    : '/api/integrations/panel-fleet/suggestions';
  return useQuery({
    queryKey: ['panel-fleet-suggestions', opts?.days ?? null],
    queryFn: () => api.get<PanelFleetSuggestionsResponse>(url),
    refetchInterval: 60_000,
    enabled: opts?.enabled ?? true,
  });
}
